import {
  DepositAnswer,
  DepositAnswerAddress,
  DepositAnswerCorrespondent,
} from '@fe/common/api/models/DepositAnswer'
import { RequestTypeEnum } from '@fe/common/api/models/enums/RequestTypeEnum'
import { DropdownOptions } from '@fe/common/components/inputs/CustomSelect'

export type TypeWithBankDetails =
  | RequestTypeEnum.WireTransfer
  | RequestTypeEnum.Sepa
  | RequestTypeEnum.Internal
  | RequestTypeEnum.CzkPayments
  | RequestTypeEnum.Internal

const joinParts = (parts: Array<string | null | undefined>) =>
  parts.filter(Boolean).join(', ')

// The non-empty parts only, so a missing country leaves no trailing comma
// (formatAddress in stringUtils does).
const addressText = (
  address: DepositAnswerAddress | undefined,
  countries: Record<number, string>
) =>
  joinParts([
    address?.line,
    address?.zip,
    address?.city,
    countries[address?.country_id],
  ])

// One correspondent per line.
const correspondentsText = (
  correspondents: DepositAnswerCorrespondent[] | undefined
) =>
  (correspondents ?? [])
    .map(({ name, bic, account }) => joinParts([name, bic, account]))
    .join('\n')

// Runs on every render, the first ones before the answer: every read starts
// at detailsData?.
export const getBankDetailsConfig = (
  detailsData: DepositAnswer | undefined,
  countries: Record<number, string>
): Record<TypeWithBankDetails, DropdownOptions> => {
  const beneficiaryName = {
    label: 'beneficiary-name',
    value: detailsData?.beneficiary?.name,
  }
  const beneficiaryAddress = {
    label: 'beneficiary-address',
    value: addressText(detailsData?.beneficiary?.address, countries),
  }
  const swift = { label: 'swift', value: detailsData?.bank?.code }
  const bankName = { label: 'bank-name', value: detailsData?.bank?.name }
  const bankAddress = {
    label: 'bank-address',
    value: addressText(detailsData?.bank?.address, countries),
  }
  const paymentDetails = {
    label: 'payment-details',
    value: detailsData?.payment_details,
  }
  const referenceNumber = {
    label: 'reference-number',
    value: detailsData?.reference,
  }

  return {
    [RequestTypeEnum.Sepa]: [
      beneficiaryName,
      beneficiaryAddress,
      { label: 'IBAN', value: detailsData?.account },
      swift,
      bankName,
      bankAddress,
      paymentDetails,
      referenceNumber,
    ],
    [RequestTypeEnum.WireTransfer]: [
      beneficiaryName,
      beneficiaryAddress,
      { label: 'account-number', value: detailsData?.account },
      swift,
      bankName,
      bankAddress,
      {
        label: 'correspondent-banks',
        value: correspondentsText(detailsData?.bank?.correspondents),
      },
      paymentDetails,
      referenceNumber,
    ],
    [RequestTypeEnum.CzkPayments]: [
      beneficiaryName,
      beneficiaryAddress,
      { label: 'account-number', value: detailsData?.domestic?.number },
      { label: 'bank-code', value: detailsData?.domestic?.bank_code },
      { label: 'IBAN', value: detailsData?.account },
      bankName,
      bankAddress,
      paymentDetails,
      { label: 'variable-symbol', value: detailsData?.reference },
    ],
    [RequestTypeEnum.Internal]: [
      { label: 'account-number', value: detailsData?.account },
    ],
  }
}
